import React from 'react'
import styled from 'styled-components'

/**
 * VersionTabs Component
 *
 * Displays horizontal tabs for switching between song content versions.
 * Provides a clean, accessible interface for version navigation in view mode.
 *
 * Features:
 * - Responsive design (wraps on small screens, scrolls on mobile)
 * - Accessibility support (ARIA labels, keyboard navigation)
 * - Auto-hides when only one version exists
 * - Visual feedback for active version
 * - Touch-friendly tap targets
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array<Object>} props.versions - Array of version objects
 * @param {string} props.versions[].versionId - Unique version identifier (UUID)
 * @param {string} props.versions[].label - Display label for the version
 * @param {string} props.activeVersionId - Currently active version ID
 * @param {Function} props.onVersionChange - Callback when version is clicked (versionId)
 *
 * @example
 * <VersionTabs
 *   versions={[
 *     { versionId: 'uuid1', label: 'Original' },
 *     { versionId: 'uuid2', label: 'Bass' }
 *   ]}
 *   activeVersionId="uuid1"
 *   onVersionChange={(versionId) => loadVersion(versionId)}
 * />
 *
 * Requirements: 3.1, 3.2, 3.5
 */

const TabsContainer = styled.div`
  display: ${({ $hide }) => $hide ? 'none' : 'flex'};
  gap: 0;
  margin-bottom: 0;
  border-bottom: 2px solid ${({ theme }) => theme.borderColor};
  
  @media(max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    
    &::-webkit-scrollbar {
      height: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.secondaryBackground};
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.borderColor};
      border-radius: 2px;
    }
  }
`

const Tab = styled.button`
  padding: 0.6rem 1.2rem;
  font-size: 0.9rem;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  color: ${({ theme }) => theme.bodyText};
  background-color: ${({ theme, $active }) => $active ? theme.inputBackground : 'transparent'};
  border: 1px solid ${({ theme, $active }) => $active ? 'transparent' : theme.borderColor};
  border-bottom: 3px solid ${({ theme, $active }) => $active ? theme.primary : 'transparent'};
  border-radius: 0.25rem 0.25rem 0 0;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
  margin-right: 0.25rem;
  
  &:hover {
    background-color: ${({ theme }) => theme.secondaryBackground};
    border-color: ${({ theme, $active }) => $active ? 'transparent' : theme.secondary};
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.secondary};
    outline-offset: -2px;
  }
  
  @media(max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
`

const VersionTabs = ({ versions, activeVersionId, onVersionChange }) => {
  // Hide tabs if there's only one version
  if (!versions || versions.length <= 1) {
    return null
  }

  return (
    <TabsContainer $hide={versions.length <= 1}>
      {versions.map((version) => (
        <Tab
          key={version.versionId}
          $active={version.versionId === activeVersionId}
          onClick={() => onVersionChange(version.versionId)}
          aria-label={`Switch to ${version.label} version`}
          aria-pressed={version.versionId === activeVersionId}
        >
          {version.label}
        </Tab>
      ))}
    </TabsContainer>
  )
}

export default VersionTabs
